import { ConfigProvider } from "antd";
import ptBR from "antd/es/locale/pt_BR";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import {
  loadTranslations,
  setLocale,
  syncTranslationWithStore,
} from "react-redux-i18n";

import * as Sentry from "@sentry/react";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

import { handleGenericApiError } from "app/api/error";
import Config from "app/config/app";
import initRedux from "app/redux";
import { AuthActions } from "app/redux/actions";
import { ACTION_AUTH_AUTHENTICATE } from "app/redux/actions/auth";
import Routes from "app/routes/index.jsx";
import { getAuthData, isAuthenticated } from "app/services/auth";

import i18nDictionary from "./i18n";

import "antd/dist/reset.css";
import "bootstrap/dist/css/bootstrap.css";
import "./styles/less/index.less";

export const store = initRedux();

export function axiosHandleUnatorizedErr() {
  store.dispatch(AuthActions.logout());
}

async function authenticated(store) {
  if (isAuthenticated()) {
    const auth = getAuthData();
    await store.dispatch(AuthActions.saveAuthData(auth));
    await store.dispatch(AuthActions.getMe());
  }
}

try {
  const { language, timezone } = Config;
  syncTranslationWithStore(store);
  store.dispatch(loadTranslations(i18nDictionary));
  store.dispatch(setLocale(language));

  const isAuthenticated = getAuthData();

  if (isAuthenticated) {
    store.dispatch({
      type: ACTION_AUTH_AUTHENTICATE,
      payload: isAuthenticated,
    });
  }

  // Set default timezone for dayjs
  dayjs.tz.setDefault(timezone);

  authenticated(store);
} catch (err) {
  handleGenericApiError(err);
}

const hostname = window.location.hostname;

const isDevelopment = ["backoffice.cpfltotal.com.br", ""].includes(hostname);

Sentry.init({
  dsn: !isDevelopment ? import.meta.env.VITE_SENTRY_DSN : undefined,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  enableLogs: true,

  // Reduz a taxa de amostragem de erros para 10%
  sampleRate: 0.1,
  // Reduz a taxa de amostragem de performance para 20%
  tracesSampleRate: 0.2,
});

const container = document.getElementById("root");
const root = createRoot(container);

const themeConfig = {
  token: {
    colorPrimary: "#0095c0",
    colorInfo: "#0095c0",
    colorSuccess: "#7cb746",
    colorError: "#e50019",
    fontFamily: "'Poppins', sans-serif",
  },
  algorithm: [], // Ensure no dark algorithm is applied
  components: {
    Menu: {
      itemBg: "#0097d2",
      itemColor: "#ffffff",
      itemHoverBg: "#0075aa",
      itemHoverColor: "#ffffff",
      itemSelectedBg: "#0075aa",
      itemSelectedColor: "#ffffff",
      subMenuItemBg: "#0075aa",
      darkItemBg: "#0097d2",
      darkItemColor: "#ffffff",
      darkItemHoverBg: "#0075aa",
      darkItemHoverColor: "#ffffff",
      darkItemSelectedBg: "#0075aa",
      darkItemSelectedColor: "#ffffff",
      darkSubMenuItemBg: "#0075aa",
      colorBgContainer: "#0097d2",
      colorText: "#ffffff",
      colorTextSecondary: "#ffffff",
      fontFamily: "'Poppins', sans-serif",
    },
    Layout: {
      siderBg: "#0097d2",
      colorBgContainer: "#0097d2",
    },
    Table: {
      fontFamily: "'Poppins', sans-serif",
    },
    Typography: {
      fontFamily: "'Poppins', sans-serif",
    },
    Button: {
      fontFamily: "'Poppins', sans-serif",
    },
    Input: {
      fontFamily: "'Poppins', sans-serif",
    },
    Select: {
      fontFamily: "'Poppins', sans-serif",
    },
  },
};

root.render(
  <Provider store={store}>
    <ConfigProvider locale={ptBR} theme={themeConfig}>
      <Routes />
    </ConfigProvider>
  </Provider>
);
